import { buildTwelveNoteScale } from "../data/twelveNoteScale";
import { IntervalQuizSettings } from "../models/IntervalQuizSettings.model";
import { Interval, IntervalNamesMap } from "../models/Intervals.model";
import { isSameMusicNote, MusicNote, toStringMusicNote } from "../models/MusicNote.model";
import type { QuizQuestion } from "../models/QuizQuestion.model";
import { findNoteByInterval } from "./findNoteByInterval";

// example outputs
// Questions: "what is the Nth interval of ____ note"
// all the same intervals?  A set of intervals of interest?
// all starting notes, or just a scale?
// all ending notes, or only notes within the same scale?
// How wrong can the wrong answers be without making the thing useless? Easy pick 3 nearby notes

// Inputs specific to interval guessing:
// static
// map of intervals from each note
//
// per quiz
// which intervals are in - Array<IntervalType>
// which starting notes are in 'all' ... 'scale'
// Questions:

// How abstract?
// 1. "2nds" -> looks at all musical notes and generates questions re: their seconds
// finding the correct answer
// finding the

export default function generateIntervalQuestions({ allowedIntervals, questionCount, allowedDirections }: IntervalQuizSettings) {
  // apply restrictions to applicable root notes
  // choose N root notes for the questions, cut out any 2x in a row
  // choose an interval from options, mildly random
  // for each chosen note + interval, figure out the answer
  // generate 3 wrong answers (nearby notes + 3)

  if (questionCount < 0) {
    throw new Error("hey stop that you goober");
  }

  const questions: Array<QuizQuestion> = [];

  const rootNoteList = [];
  const intervalList = [];
  const directionList = [];
  let previousRootNote;
  for (var i = 0; i < questionCount; i++) {
    const rootNote = chooseSemiRandomNote({
      disallowedNotes: [previousRootNote],
    });
    const direction = chooseRandomDirection({ allowedDirections });
    const interval = chooseRandomInterval({ allowedIntervals });

    // these lists perhaps don't need to exist.  Just build a Question
    rootNoteList.push(rootNote);
    intervalList.push(interval);
    directionList.push(direction);
    previousRootNote = rootNote;

    const correctAnswer = findNoteByInterval({ rootNote, interval, direction });

    const wrongAnswers = chooseNearbyWrongAnswerNotes({
      rootNote,
      correctAnswer,
    });

    const noteName = rootNote.name || [rootNote.flatName, rootNote.sharpName][Math.floor(Math.random() * 2)];

    const questionText = `Which note is a ${IntervalNamesMap[interval]} ${direction} ${noteName} ?`;

    questions.push({
      correctAnswer: toStringMusicNote(correctAnswer),
      questionText,
      titleText: "Interval",
      wrongAnswers: wrongAnswers.map(toStringMusicNote),
    });
  }

  return questions;
}

function chooseNearbyWrongAnswerNotes({
  rootNote,
  correctAnswer,
  numWrongAnswers = 3,
}: {
  rootNote: MusicNote;
  correctAnswer: MusicNote;
  numWrongAnswers?: number;
}): Array<MusicNote> {
  const twelveNoteScale = buildTwelveNoteScale(); // or use immutablejs
  const rootIndex = twelveNoteScale.findIndex((note) => isSameMusicNote(note, rootNote));
  const correctIndex = twelveNoteScale.findIndex((note) => isSameMusicNote(note, correctAnswer));
  const wrongIndices: Array<number> = [];
  while (wrongIndices.length < numWrongAnswers) {
    let proposedWrongIndex = rootIndex;
    // brute-force N unique wrong answers
    const priorOptions = [...wrongIndices, rootIndex, correctIndex];
    while (priorOptions.includes(proposedWrongIndex)) {
      const veryPositiveRoot = rootIndex + 12; // +12 prevents negative indices, we can then %12 at the end
      const adjustment = Math.ceil(Math.random() * numWrongAnswers) * chooseRandomPositiveOrNegative();
      proposedWrongIndex = (veryPositiveRoot + adjustment) % 12;
    }
    wrongIndices.push(proposedWrongIndex);
  }
  return wrongIndices.map((ind) => twelveNoteScale[ind]);
}

function chooseRandomPositiveOrNegative() {
  return [1, -1][Math.floor(Math.random() * 2)];
}

function chooseRandomInterval({ allowedIntervals }: Pick<IntervalQuizSettings, "allowedIntervals">): Interval {
  return allowedIntervals[Math.floor(Math.random() * allowedIntervals.length)];
}

// TODO: scale restrictions
function chooseSemiRandomNote({ disallowedNotes }: { disallowedNotes: Array<MusicNote | undefined> }) {
  const twelveNoteScale = buildTwelveNoteScale();
  let nextNoteIndex = Math.floor(Math.random() * 12);
  let nextNote = twelveNoteScale[nextNoteIndex];
  if (disallowedNotes.some((n) => isSameMusicNote(n, nextNote))) {
    // lazy, not actually correct in general but OK when we're only preventing the same note in a row
    nextNote = twelveNoteScale[(nextNoteIndex + 2) % 12];
  }
  return nextNote;
}

type AboveOrBelow = "above" | "below";
function chooseRandomDirection({ allowedDirections }: Pick<IntervalQuizSettings, "allowedDirections">): AboveOrBelow {
  if (allowedDirections.down && allowedDirections.up) {
    return ["above", "below"][Math.floor(Math.random() * 2)] as AboveOrBelow;
  }
  if (allowedDirections.down) {
    return "below";
  }
  return "above";
}
